import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  BookOpen,
  Clock,
  Award,
  Layers,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Search,
  CheckCircle2,
  GraduationCap,
  Building2,
  Target,
  ExternalLink,
  Plus,
  Loader2,
  AlertCircle,
  Hash,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as api from "@/src/api/sdk.gen";
import type {
  B2bCourseTrackOutlineResponse,
  B2bCourseTrackModuleOutline,
} from "@/src/api/types.gen";
import type { CoursePreview } from "../../types";

export interface CourseTrackOutlineModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  course: CoursePreview | null;
  onAssignToWorkspace?: (course: CoursePreview) => void;
}

export const CourseTrackOutlineModal: React.FC<CourseTrackOutlineModalProps> = ({
  isOpen,
  onClose,
  slug,
  course,
  onAssignToWorkspace,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [trackData, setTrackData] = useState<B2bCourseTrackOutlineResponse | null>(null);

  // Search & Accordion State
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedModuleIds, setExpandedModuleIds] = useState<Set<number>>(new Set());

  // Fetch track data on open
  useEffect(() => {
    if (!isOpen || !course || !slug) {
      setTrackData(null);
      setError(null);
      return;
    }

    let isCancelled = false;
    setLoading(true);
    setError(null);

    const fetchTrack = async () => {
      try {
        const { data, error: apiErr } =
          await api.getCourseTrackOutlineApiV1B2bSlugCoursesCourseIdTrackGet({
            path: {
              slug,
              course_id: course.uuid || String(course.id),
            },
          });

        if (isCancelled) return;

        if (apiErr || !data) {
          setError(
            (apiErr as any)?.detail ||
              "Unable to load course track outline. Please try again."
          );
          return;
        }

        setTrackData(data);
        // Expand first 2 modules by default
        if (data.modules && data.modules.length > 0) {
          setExpandedModuleIds(
            new Set(data.modules.slice(0, 2).map((m: B2bCourseTrackModuleOutline) => m.id))
          );
        }
      } catch (err: any) {
        if (!isCancelled) {
          setError(err.message || "Failed to load course track outline.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchTrack();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, course, slug]);

  const toggleModule = (moduleId: number) => {
    setExpandedModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    if (!trackData?.modules) return;
    setExpandedModuleIds(new Set(trackData.modules.map((m) => m.id)));
  };

  const handleCollapseAll = () => {
    setExpandedModuleIds(new Set());
  };

  // Filter modules and lessons based on search query
  const filteredModules = useMemo(() => {
    if (!trackData?.modules) return [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return trackData.modules;

    return trackData.modules.filter((module) => {
      const moduleMatch = module.title.toLowerCase().includes(query);
      const lessonMatch = module.lessons.some(
        (lesson) =>
          lesson.title.toLowerCase().includes(query) ||
          lesson.key_concepts.some((concept) =>
            concept.toLowerCase().includes(query)
          )
      );
      return moduleMatch || lessonMatch;
    });
  }, [trackData?.modules, searchQuery]);

  if (!isOpen || !course) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header / Hero */}
        <div className="relative bg-slate-900 border-b border-slate-800 text-white p-6 sm:p-8 shrink-0 overflow-hidden">
          {/* Background image overlay */}
          {course.cover_image_url && (
            <div
              className="absolute inset-0 bg-cover bg-center opacity-15 mix-blend-luminosity"
              style={{ backgroundImage: `url(${course.cover_image_url})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-indigo-950/80 to-slate-900/90" />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition cursor-pointer z-10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative z-10 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 rounded-lg text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                {trackData?.track || "Enterprise Curriculum Track"}
              </span>
              {trackData?.level && (
                <span className="px-3 py-1 bg-white/10 border border-white/20 text-slate-200 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                  <GraduationCap className="w-3.5 h-3.5 text-slate-300" />
                  {trackData.level}
                </span>
              )}
            </div>

            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                {course.title}
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-slate-300 line-clamp-3 leading-relaxed max-w-3xl">
                {trackData?.description ||
                  course.description ||
                  "Comprehensive enterprise training curriculum with structured modules, real-world case studies, and knowledge checkpoints."}
              </p>
            </div>

            {/* Metric Pills */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-xs text-white">
                <Layers className="w-4 h-4 text-indigo-400" />
                <span className="font-semibold">
                  {trackData?.total_modules ?? course.total_modules} Modules
                </span>
              </div>
              <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-xs text-white">
                <BookOpen className="w-4 h-4 text-emerald-400" />
                <span className="font-semibold">
                  {trackData?.total_lessons ?? course.total_lessons} Lessons
                </span>
              </div>
              {trackData?.estimated_duration_hours && (
                <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-xs text-white">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="font-semibold">
                    ~{trackData.estimated_duration_hours} Hours
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-xs text-white">
                <Award className="w-4 h-4 text-purple-400" />
                <span className="font-semibold">Interactive Assessments</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-center space-y-3">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              <p className="text-sm font-semibold text-slate-700">
                Loading complete course syllabus & curriculum track...
              </p>
              <p className="text-xs text-slate-400 max-w-sm">
                Retrieving detailed module outlines, lesson objectives, and learning concepts.
              </p>
            </div>
          ) : error ? (
            <div className="p-6 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-500" />
              <div>
                <h4 className="text-sm font-bold">Could not load track outline</h4>
                <p className="text-xs mt-1 text-red-600">{error}</p>
              </div>
            </div>
          ) : (
            <>
              {/* Organizational Alignment & Learning Outcomes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Targeted Departments */}
                {trackData?.departments && trackData.departments.length > 0 && (
                  <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5 mb-2.5">
                      <Building2 className="w-4 h-4 text-indigo-600" />
                      Target Departments
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {trackData.departments.map((dept, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-medium shadow-2xs"
                        >
                          {dept}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Career Goals / Applied Skills */}
                {trackData?.future_goals && trackData.future_goals.length > 0 && (
                  <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5 mb-2.5">
                      <Target className="w-4 h-4 text-emerald-600" />
                      Upskilling Pathways
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {trackData.future_goals.map((goal, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-xs font-medium shadow-2xs"
                        >
                          {goal}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Core Learning Outcomes / What This Course Covers */}
              {trackData?.key_takeaways && trackData.key_takeaways.length > 0 && (
                <div className="p-5 bg-gradient-to-br from-indigo-50/60 via-purple-50/30 to-white border border-indigo-100 rounded-2xl">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-950 flex items-center gap-1.5 mb-3">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    Key Competencies & Concepts Covered
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {trackData.key_takeaways.map((concept, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-xs text-slate-700 leading-snug"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-indigo-600 shrink-0 mt-0.5" />
                        <span>{concept}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Curriculum Breakdown */}
              <div className="space-y-4 pt-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Layers className="w-5 h-5 text-indigo-600" />
                      Course Curriculum & Syllabus
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Explore every module, lesson objectives, and topics your team will master.
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleExpandAll}
                      className="px-2.5 py-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition cursor-pointer"
                    >
                      Expand All
                    </button>
                    <span className="text-slate-300">•</span>
                    <button
                      type="button"
                      onClick={handleCollapseAll}
                      className="px-2.5 py-1 text-xs font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition cursor-pointer"
                    >
                      Collapse All
                    </button>
                  </div>
                </div>

                {/* Search / Filter Input */}
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search modules, lessons, or topics..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Modules Accordion List */}
                {filteredModules.length === 0 ? (
                  <div className="p-8 text-center bg-slate-50 rounded-2xl border border-slate-200">
                    <p className="text-xs text-slate-500">
                      No modules or lessons matched &quot;{searchQuery}&quot;
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredModules.map((module, idx) => {
                      const isExpanded = expandedModuleIds.has(module.id);
                      return (
                        <div
                          key={module.id}
                          className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-2xs transition hover:border-slate-300"
                        >
                          {/* Module Header Button */}
                          <button
                            type="button"
                            onClick={() => toggleModule(module.id)}
                            className="w-full p-4 flex items-center justify-between text-left bg-slate-50/60 hover:bg-slate-100/70 transition cursor-pointer gap-3"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-bold flex items-center justify-center shrink-0">
                                {idx + 1}
                              </span>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="text-xs sm:text-sm font-bold text-slate-900 truncate">
                                    {module.title}
                                  </h4>
                                  {module.level && (
                                    <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-[10px] font-semibold">
                                      {module.level}
                                    </span>
                                  )}
                                  {module.has_quiz && (
                                    <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[10px] font-semibold flex items-center gap-1">
                                      <Award className="w-3 h-3 text-amber-500" />
                                      Assessment
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                                  <span>{module.total_lessons} Lessons</span>
                                  {module.duration && (
                                    <>
                                      <span>•</span>
                                      <span>{module.duration}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="shrink-0 text-slate-400">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </div>
                          </button>

                          {/* Lessons List */}
                          {isExpanded && (
                            <div className="divide-y divide-slate-100 border-t border-slate-100 bg-white">
                              {module.lessons.map((lesson, lessonIdx) => (
                                <div
                                  key={lesson.id}
                                  className="p-4 hover:bg-slate-50/60 transition space-y-2"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2.5">
                                      <span className="text-[11px] font-mono font-semibold text-slate-400 shrink-0 mt-0.5">
                                        {idx + 1}.{lessonIdx + 1}
                                      </span>
                                      <p className="text-xs font-semibold text-slate-800 leading-snug">
                                        {lesson.title}
                                      </p>
                                    </div>
                                    <span className="shrink-0 text-[11px] font-medium text-slate-400 flex items-center gap-1">
                                      <Clock className="w-3 h-3 text-slate-300" />
                                      {lesson.duration || "15 min"}
                                    </span>
                                  </div>

                                  {/* Key Concepts taught in this lesson */}
                                  {lesson.key_concepts && lesson.key_concepts.length > 0 && (
                                    <div className="pl-6 flex flex-wrap gap-1.5">
                                      {lesson.key_concepts.map((concept, cIdx) => (
                                        <span
                                          key={cIdx}
                                          className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium"
                                        >
                                          {concept}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Modal Footer Actions */}
        <div className="p-4 sm:p-5 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-500 text-center sm:text-left">
            Course track can be assigned to any workspace with custom seat limits and compliance rules.
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
            {onAssignToWorkspace && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onAssignToWorkspace(course);
                }}
                className="flex-1 sm:flex-initial px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs transition cursor-pointer active:scale-95"
              >
                <Plus className="w-4 h-4" />
                Assign to Workspace
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate(`/courses/${course.uuid}`)}
              className="px-3.5 py-2.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition cursor-pointer shadow-2xs"
            >
              Open as Learner
              <ExternalLink className="w-3.5 h-3.5" />
            </button>

            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 bg-slate-200/80 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
